// ═══════════════════════════════════════════════════════════════════════════════
// Destination preset library — one-tap starter cards for the tenant
// branding > Destinations tab. Tenants can pick from these to bootstrap
// a showcase quickly, then edit the copy / image / link to taste.
// They keep the existing "Add destination" flow for fully-custom entries.
//
// Image URLs are intentionally LEFT BLANK — tenants supply their own
// (avoids hotlinking risks, and gives them a chance to use brand-
// consistent imagery). The description copy is the same voice we
// shipped in the destination card library so the cards read like a
// curated travel-magazine set out of the box.
// ═══════════════════════════════════════════════════════════════════════════════

export interface DestinationPreset {
  /** Stable key for "already added?" lookup against existing rows by name. */
  key:         string;
  emoji:       string;
  name:        string;
  subtitle:    string;
  description: string;
  price_range: string;
  best_season: string;
  tags:        string[];
}

export const DESTINATION_PRESETS: DestinationPreset[] = [
  {
    key: 'bali',
    emoji: '🏝️',
    name: 'Bali, Indonesia',
    subtitle: 'Three trips stacked in one island',
    description:
      "Rice terraces, surf beaches, and temple-lit mornings. Bali rewards travelers who slow down — wake for sunrise yoga in Ubud, dive coral walls off Amed, and end the day with sambal-spiced everything.",
    price_range: '$$',
    best_season: 'April – October (dry)',
    tags: ['beach', 'culture', 'yoga', 'foodie', 'adventure'],
  },
  {
    key: 'spain',
    emoji: '🇪🇸',
    name: 'Spain',
    subtitle: 'Eat late, walk longer',
    description:
      "Tapas crawls, Moorish palaces, and sun-bleached coasts. Spain rewards travelers who eat late and walk longer — vermouth in Madrid's old town, the Alhambra at first light, an afternoon lost to pintxos in San Sebastián.",
    price_range: '$$',
    best_season: 'April – June, Sept – Oct',
    tags: ['food', 'history', 'architecture', 'beach', 'culture'],
  },
  {
    key: 'italy',
    emoji: '🇮🇹',
    name: 'Italy',
    subtitle: 'Linger longer at the table',
    description:
      "Vespa horns, Renaissance churches, and pasta cut by hand. Italy rewards travelers who linger — sip espresso standing at a Roman bar, drive the Amalfi Coast at golden hour, and let dinner stretch past midnight in a Tuscan farmhouse.",
    price_range: '$$$',
    best_season: 'April – June, Sept – Oct',
    tags: ['food', 'art', 'history', 'romance', 'coastline'],
  },
  {
    key: 'japan',
    emoji: '🇯🇵',
    name: 'Japan',
    subtitle: 'Neon and ritual in the same trip',
    description:
      "Neon alleys, ancient shrines, and bullet trains that arrive to the second. Japan rewards travelers who slow down for the small ritual — slurp ramen at a six-seat counter, walk Kyoto's bamboo grove at dawn, and lose an evening to Tokyo's lantern-lit yokocho.",
    price_range: '$$$',
    best_season: 'March – May, Oct – Nov',
    tags: ['culture', 'foodie', 'tech', 'temples', 'transit'],
  },
  {
    key: 'thailand',
    emoji: '🇹🇭',
    name: 'Thailand',
    subtitle: 'Eat from the street',
    description:
      "Glittering temples, longtail boats, and night-market chaos. Thailand rewards travelers who eat from the street — pad krapow in a Bangkok soi, kayak through Krabi's limestone bays, and learn that mango sticky rice is its own food group.",
    price_range: '$',
    best_season: 'Nov – Feb (dry)',
    tags: ['beach', 'foodie', 'islands', 'budget', 'temples'],
  },
  {
    key: 'greece',
    emoji: '🇬🇷',
    name: 'Greece',
    subtitle: 'Hop between Aegean blues',
    description:
      "Whitewashed villages, ancient ruins, and Aegean blue everywhere. Greece rewards travelers who island-hop — climb the Acropolis at dawn, ferry to a quieter Cycladic port, and end the day with grilled octopus by a salt-bleached harbor.",
    price_range: '$$',
    best_season: 'May – June, Sept – Oct',
    tags: ['islands', 'history', 'beach', 'foodie', 'sailing'],
  },
];
